// React Compiler opt-out: the query reads below are against the mutable mandark
// cache and must re-evaluate on every render — compiling this hook would memoize
// them on the (stable) `queries` identity and freeze `isReady` at its first value.
'use no memo';

import {useEffect, useReducer} from 'react';

import {getActiveState} from 'client/framework';

/**
 * @typedef {import('../../albert-io/json-api-framework/request/builder').QueryBuilder} QueryBuilder
 */

/**
 * @typedef {Object} ReturnValue
 * @property {boolean} isReady - Whether or not all queries are populated
 * @property {*[]} resources - An array of resources in the order that they were passed in
 */

/**
 * Historically this hook had no subscription and relied on the app's global
 * top-down re-render to pick up resource arrival. Compiled (React Compiler)
 * consumers skip those parent-driven re-renders, so the hook now subscribes to
 * the state tree's change event — which mandark request settles reach via
 * CHANGE_CALLBACK → getActiveState().forceUpdate() (see App.react.js) — and
 * re-renders its consumer directly.
 *
 * @param {QueryBuilder[]} queries
 * @returns {ReturnValue}
 */
const useMandarkQueries = (queries) => {
  const [, forceUpdate] = useReducer((n) => n + 1, 0);

  useEffect(() => {
    const onChange = () => forceUpdate();
    getActiveState().on('change', onChange);
    return () => {
      getActiveState().off('change', onChange);
    };
  }, []);

  const isReady = queries.every((query) => query.isResourcePopulated());
  const resources = queries.map((query) => query.getResource());

  return {
    isReady,
    resources
  };
};

export default useMandarkQueries;
